import { createSelector } from 'reselect';
import { validate } from '@deckee/utilities';
import { AppState } from '../..';
import { ROUTE_ACCOUNT_SETTINGS } from '../../../route/route-names';
import { Vessel } from './models';

// eslint-disable-next-line import/prefer-default-export
export const selectBackRoute = (state: AppState): string =>
  state.vessels.backRoute || ROUTE_ACCOUNT_SETTINGS;

export const validateLoloLoggedOnWatercraft = (watercraft: Vessel): void => {
  // Mandatory for all
  validate.requiredString(watercraft.name, 'name');
  validate.requiredString(watercraft.make_model, 'make_model');
  validate.requiredString(watercraft.hullColor, 'hullColor');

  // Vessel Registration
  if (
    ['motorboat', 'personal watercraft'].includes(watercraft.watercraft_type)
  ) {
    validate.requiredString(watercraft.registration, 'registration');
  }

  // Radio equipment
  if (['motorboat', 'sailboat'].includes(watercraft.watercraft_type)) {
    validate.requiredString(watercraft.radioOptions, 'radioOptions');
  }

  // Vessel specs
  if (['motorboat', 'sailboat'].includes(watercraft.watercraft_type)) {
    validate.requiredNumber(watercraft.vessel_length, 'vessel_length');
    validate.requiredString(watercraft.length_units, 'length_units');
  }

  // Vessel store on a trailer
  if (
    ['motorboat', 'sailboat', 'personal-watercraft'].includes(
      watercraft.watercraft_type,
    )
  ) {
    validate.boolean(watercraft.storedOnTrailer, 'storedOnTrailer');
    if (watercraft.storedOnTrailer) {
      validate.requiredString(
        watercraft.trailerRegistration,
        'trailerRegistration',
      );
    }
  }
  // Optionals
  validate.string(watercraft.call_sign, 'call_sign');
};

export const isValidLoggedOnVessel = (vessel: Vessel): boolean => {
  try {
    validateLoloLoggedOnWatercraft(vessel);
    return true;
  } catch (error) {
    return false;
  }
};

export const selectedFloatPlanId = createSelector(
  (state: AppState) => state.floatPlan,
  (floatPlan) => floatPlan && floatPlan && floatPlan?.id,
);

export const selectedFloatPlanVesselId = createSelector(
  (state: AppState) => state.floatPlan,
  (floatPlan) =>
    floatPlan && floatPlan && floatPlan?.vessel && floatPlan?.vessel.id,
);

export const selectedFloatPlanLoggedOn = createSelector(
  (state: AppState) => state.floatPlan,
  (floatPlan) => floatPlan && floatPlan.loloEnabled,
);

export const selectAllVessels = (state: AppState): Vessel[] =>
  state.vessels.vessels;

export const selectSelectAllVessel = createSelector(
  selectAllVessels,
  selectedFloatPlanLoggedOn,
  (vessels, isFloatPlanLoggedOn): Vessel[] =>
    vessels.map((vessel) => ({
      ...vessel,
      isInvalidLoggedOnVessel:
        isFloatPlanLoggedOn && !isValidLoggedOnVessel(vessel),
    })),
);
export const selectSelectedVessel = createSelector(
  selectSelectAllVessel,
  (state: AppState) => state.vessels.selectedVesselId,
  (vessels, vesselId) => vessels.find((vessel) => vessel.id === vesselId),
);

export const selectSelectedOrFirstVessel = createSelector(
  selectSelectAllVessel,
  selectSelectedVessel,
  (vessels, selectedVessel) => selectedVessel || vessels[0],
);

export const selectIsSelectedLoggedOnVesselInvalid = createSelector(
  selectedFloatPlanLoggedOn,
  selectSelectedVessel,
  (isFloatPlanLoggedOn, vessel) => {
    if (vessel && isFloatPlanLoggedOn) {
      return !isValidLoggedOnVessel(vessel);
    }
    return false;
  },
);
